import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from '@/hooks/use-toast';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Company } from '@/types/company';

interface AssignCompaniesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (companies: Company[]) => Promise<void>;
  selectedCompanies?: Company[];
}

export function AssignCompaniesModal({
  isOpen,
  onClose,
  onAssign,
  selectedCompanies = []
}: AssignCompaniesModalProps) {
  const [selected, setSelected] = useState<Company[]>(selectedCompanies);
  const [searchTerm, setSearchTerm] = React.useState("");
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // selectedCompanies değiştiğinde selected state'ini güncelle
  useEffect(() => {
    setSelected(selectedCompanies);
  }, [selectedCompanies]);

  // Firmaları getir
  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['companies'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/companies`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Firma verileri alınamadı');
        }
        
        const data = await response.json();
        console.log('Gelen firma verileri:', data);
        return data;
      } catch (error) {
        console.error('Firma verileri alınırken hata:', error);
        throw error;
      }
    }
  });

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onAssign(selected);
      toast({
        title: "Başarılı",
        description: "Firmalar başarıyla atandı.",
      });
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Firmalar atanırken bir hata oluştu.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleCompany = (company: Company) => {
    setSelected(prev =>
      prev.some(c => c._id === company._id)
        ? prev.filter(c => c._id !== company._id)
        : [...prev, company]
    );
  };

  const isCompanySelected = (company: Company) => {
    return selected.some(c => c._id === company._id);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Firmalara Ata</DialogTitle>
          <DialogDescription>
            Bu playlist'i atamak istediğiniz firmaları seçin.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="search">Firma Ara</Label>
            <div className="flex gap-2">
              <Input
                id="search"
                placeholder="Firma adı yazın..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button 
                variant="outline" 
                onClick={() => setSelected(searchTerm ? [...filteredCompanies] : [...companies])} 
                type="button"
              >
                Tümü
              </Button>
            </div>
          </div>

          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-2">
              {filteredCompanies.map((company) => (
                <div
                  key={company._id}
                  onClick={() => toggleCompany(company)}
                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-accent ${
                    isCompanySelected(company) ? 'bg-accent' : ''
                  }`}
                >
                  <span>{company.name}</span>
                  {isCompanySelected(company) && (
                    <Check className="h-4 w-4" />
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Atanıyor...' : 'Ata'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
